import { Trophy, ShieldCheck, ThumbsUp, ThumbsDown, ClipboardCheck } from "lucide-react";
import { money } from "@/lib/api";
import { ScoreRing, VerdictBadge } from "@/components/ScoreRing";
import { LiveSiteData } from "@/components/LiveSiteData";
import { SiteMap } from "@/components/SiteMap";

const pct = (v) => (typeof v === "number" ? `${v}%` : "—");
const METRICS = [
  ["Category", (r) => r.category || "—"],
  ["Data source", (r) => (r.data_source === "FDD" ? <span className="inline-flex items-center gap-1 text-emerald-300"><ShieldCheck className="h-3.5 w-3.5" />Verified FDD</span> : <span className="text-slate-400">AI estimate</span>)],
  ["Initial franchise fee", (r) => money(r.initial_fee)],
  ["Royalty", (r) => pct(r.royalty_pct)],
  ["Marketing fund", (r) => pct(r.marketing_pct)],
  ["Total investment (Item 7)", (r) => `${money(r.investment_low)} – ${money(r.investment_high)}`],
  ["Avg unit revenue / yr", (r) => money(r.avg_unit_revenue)],
  ["Royalties + fund / yr", (r) => money(r.royalty_and_fund_cost)],
  ["Net profit after fees / yr", (r) => money(r.net_profit_after_fees)],
  ["Payback", (r) => (r.payback_months ? `${r.payback_months} mo` : "—")],
  ["5-yr ROI after royalties", (r) => pct(r.roi_percent)],
  ["Territory fit", (r) => (r.territory_fit != null ? `${r.territory_fit}/10` : "—")],
  ["Feasibility score", (r) => `${r.score}/100`],
];
const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

const Matrix = ({ res }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-800" data-testid="compare-matrix">
    <table className="w-full text-sm min-w-[640px]">
      <thead><tr className="bg-slate-900/80">
        <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold w-56">Metric</th>
        {res.brands.map((r) => <th key={r.brand} className={`text-left px-4 py-3 font-display font-semibold ${r.brand === res.winner ? "bg-emerald-500/10 text-emerald-200" : ""}`}>{r.brand}{r.brand === res.winner && <Trophy className="inline h-4 w-4 ml-1.5 text-amber-300" />}</th>)}
      </tr></thead>
      <tbody>
        {METRICS.map(([label, f]) => (
          <tr key={label} className="border-t border-slate-800/80">
            <td className="px-4 py-2.5 text-slate-400">{label}</td>
            {res.brands.map((r) => <td key={r.brand} data-testid={`compare-cell-${slug(label)}-${slug(r.brand)}`} className={`px-4 py-2.5 num ${r.brand === res.winner ? "bg-emerald-500/5" : ""}`}>{f(r)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const List = ({ items, icon: Icon, tone }) => (
  <ul className="space-y-1.5">{(items || []).map((x, i) => <li key={i} className="flex gap-2 text-sm text-slate-300"><Icon className={`h-4 w-4 mt-0.5 shrink-0 ${tone}`} />{x}</li>)}</ul>
);

const BrandCard = ({ r, winner }) => (
  <div className={`rounded-xl border p-6 ${winner ? "border-emerald-500/50 bg-emerald-500/5" : "border-slate-800 bg-slate-900/60"}`} data-testid={`compare-brand-${slug(r.brand)}`}>
    <div className="flex items-center gap-4 mb-4">
      <ScoreRing score={r.score} size={56} />
      <div><p className="font-display text-lg font-bold">{r.brand}</p><VerdictBadge verdict={r.verdict} /></div>
    </div>
    {r.fit_note && <p className="text-sm text-slate-300 mb-4">{r.fit_note}</p>}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><List items={r.pros} icon={ThumbsUp} tone="text-emerald-400" /><List items={r.cons} icon={ThumbsDown} tone="text-rose-400" /></div>
  </div>
);

export const ComparisonView = ({ c }) => {
  const res = c.result;
  return (
    <div className="space-y-8" data-testid="comparison-view">
      <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-6 flex gap-4" data-testid="compare-winner">
        <Trophy className="h-8 w-8 text-amber-300 shrink-0" />
        <div><p className="text-xs uppercase tracking-[0.2em] text-emerald-300 font-semibold mb-1">Recommended for this address</p>
          <p className="font-display text-2xl font-bold mb-2" data-testid="compare-winner-name">{res.winner}</p><p className="text-sm text-slate-300">{res.winner_reason}</p></div>
      </div>
      <div className="space-y-3 text-slate-300">{(res.summary || "").split("\n\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}</div>
      <Matrix res={res} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">{res.brands.map((r) => <BrandCard key={r.brand} r={r} winner={r.brand === res.winner} />)}</div>
      {c.site_data?.lat && <div className="space-y-6"><h2 className="font-display text-xl font-bold">Site snapshot</h2><SiteMap address={c.address} height={240} testId="compare-map" /><LiveSiteData data={c.site_data} /></div>}
      {!!res.considerations?.length && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6" data-testid="compare-considerations">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Verify before signing</p><List items={res.considerations} icon={ClipboardCheck} tone="text-cyan-300" />
        </div>
      )}
      <p className="text-xs text-slate-500">Figures marked AI estimate come from public knowledge and must be checked against each brand's current FDD. Projections are estimates, not earnings claims.</p>
    </div>
  );
};
